import useLocalStorage from './useLocalStorage';
import { useMemo } from 'react';
import {
    getLocalStorageItem,
    removeLocalStorageItem,
    setLocalStorageItem,
} from '../utils/localStorage';
import { LiveChatStateRecord } from './useLiveChatState';

const CONVERSATION_ID_KEY = 'grace_conversation_id';
const CONVERSATION_STORAGE_KEY = 'grace_conversation_storage';

type ConversationStorageType = {
    conversationId?: string;
    liveChatState?: LiveChatStateRecord;
};
type ConversationStorageKeyType = keyof ConversationStorageType;

/**
 * Initialize conversation storage with a new conversation id
 * @param {string} newConversationId - The new conversation id to initialize the storage with
 */
export const initConversationStorage = (newConversationId: string) => {
    const conversationId = getLocalStorageItem(CONVERSATION_ID_KEY);

    if (conversationId !== newConversationId) {
        setLocalStorageItem(CONVERSATION_STORAGE_KEY, {
            conversationId: newConversationId,
        });
        setLocalStorageItem(CONVERSATION_ID_KEY, newConversationId);
    }
};

/**
 * Clear the conversation storage by removing the conversation storage key and conversation id key from local storage.
 */
export const clearConversationStorage = () => {
    removeLocalStorageItem(CONVERSATION_STORAGE_KEY);
    removeLocalStorageItem(CONVERSATION_ID_KEY);
};

export const getConversationStorage = (): ConversationStorageType => {
    // @ts-ignore
    let currentStorage = getLocalStorageItem<ConversationStorageType>(
        CONVERSATION_STORAGE_KEY
    );
    if (!currentStorage || typeof currentStorage === 'string') {
        currentStorage = {};
    }
    return currentStorage;
};

// a function signature where value is never returned as null
export default function useConversationStorage<T>(
    key: ConversationStorageKeyType,
    defaultValue: T
): [T, (value: T) => void, () => void];

/**
 * Stores and retrieves a value from conversation storage using the provided key.
 *
 * @template T - The type of the value to be stored/retrieved.
 *
 * @param {string} key - The key to identify the value in conversation storage.
 * @param {T | null} [defaultValue=null] - The default value to be returned if the value does not exist in conversation storage.
 *
 * @returns {[T | null, (value: T) => void, () => void]} - An array containing the following:
 * - The stored value or the default value if the value does not exist in conversation storage.
 * - A function to set the value in conversation storage.
 * - A function to remove the value from conversation storage.
 */
export default function useConversationStorage<T>(
    key: ConversationStorageKeyType,
    defaultValue: T | null = null
): [T | null, (value: T) => void, () => void] {
    const [conversationStorage, setConversationStorage] =
        useLocalStorage<ConversationStorageType>(CONVERSATION_STORAGE_KEY);

    const conversationStorageObject: ConversationStorageType =
        conversationStorage && typeof conversationStorage === 'object'
            ? conversationStorage
            : {};

    const setValue = (value: T) => {
        setConversationStorage({
            ...getConversationStorage(),
            [key]: value,
        });
    };

    const removeValue = () => {
        if (key in conversationStorageObject) {
            delete conversationStorageObject[key];
            setConversationStorage(conversationStorageObject);
        }
    };

    const returnValue = useMemo(() => {
        if (key in conversationStorageObject) {
            return conversationStorageObject[key] as T | null;
        }

        return defaultValue;
    }, [conversationStorageObject, key]);

    return [returnValue, setValue, removeValue];
}
